import React, { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../lib/apiClient';
import CategoriesPanel from './directory/CategoriesPanel';
import ListingsPanel from './directory/ListingsPanel';

/**
 * Business Directory admin. Tabs: accepted listings, public submissions waiting
 * for review (with a count badge), and the categories listings sit in. The whole
 * screen is behind the `directory_enabled` flag (see AdminLayout); the API
 * independently answers 404 when it is off.
 */
const TABS = [
  { id: 'listings', label: 'Listings' },
  { id: 'pending', label: 'Pending' },
  { id: 'categories', label: 'Categories' },
];

const AdminDirectory = () => {
  const [tab, setTab] = useState('listings');
  const [pendingCount, setPendingCount] = useState(0);

  const refreshPending = useCallback(async () => {
    try {
      const res = await apiClient.get('/directory/pending-count');
      setPendingCount(Number(res.data?.pending) || 0);
    } catch {
      // A failed count only hides the badge; the tabs themselves still work.
    }
  }, []);

  useEffect(() => { refreshPending(); }, [refreshPending]);

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6" data-testid="admin-directory">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900">Business Directory</h1>
        <p className="text-gray-500 text-sm mt-1 mb-4">
          Free listings for organisations that help people in need.
        </p>

        <div className="flex gap-2 mb-5 border-b border-gray-200 overflow-x-auto" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => { setTab(t.id); refreshPending(); }}
              data-testid={`directory-tab-${t.id}`}
              className={`px-4 py-3 min-h-[44px] text-sm font-semibold border-b-2 -mb-px whitespace-nowrap ${
                tab === t.id
                  ? 'border-[#FF6B00] text-[#FF6B00]'
                  : 'border-transparent text-gray-500 hover:text-gray-900'
              }`}
            >
              {t.label}
              {t.id === 'pending' && pendingCount > 0 && (
                <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#FF6B00] text-[#1a1c1b] text-xs font-bold" data-testid="directory-pending-badge">
                  {pendingCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {tab === 'listings' && <ListingsPanel key="listings" onChanged={refreshPending} />}
        {tab === 'pending' && <ListingsPanel key="pending" mode="pending" onChanged={refreshPending} />}
        {tab === 'categories' && <CategoriesPanel />}
      </div>
    </div>
  );
};

export default AdminDirectory;
